import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Bot,
    Briefcase,
    Eye,
    Loader2,
    MapPin,
    Radar,
    Search,
    Send,
    Sparkles,
    Users,
    type LucideIcon,
} from 'lucide-react';
import {
    jobApi,
    type JobPosting,
    type JobRosterSummary,
    type JobSourcingSummary,
    type JobOutreachSummary,
} from '@/modules/job/services/job.api';
import { getApiErrorMessage } from '@/lib/http';
import { SidebarExpandButton } from '@/components/SidebarExpandButton';
import { RainbowBeamsBackdrop } from '@/components/RainbowBeamsBackdrop';

/**
 * /jobs — the default-org roster. Two calls total: the list paints off one
 * /job call, then ONE batch /job/roster-summary call fills every card's
 * sourcing + outreach lane (summary failure is non-fatal). Lanes render ONLY
 * when that module is set up on the job — a bare job stays a clean card.
 * Design mirrors the searches-v3 glass-over-rainbow artifact language.
 */

/* ── Palette (searches-v3 glass language) ─────────────────────── */

type Tone = 'green' | 'amber' | 'red' | 'gray' | 'blue' | 'coral';

const TONE_CLASS: Record<Tone, string> = {
    green: 'bg-[#EAF7F0] text-[#1E7F5F]',
    amber: 'bg-[#FFF4DF] text-[#A56517]',
    red: 'bg-[#FFF0EE] text-[#B83D2F]',
    gray: 'bg-[#F0EFEB] text-[#69727C]',
    blue: 'bg-[#EAF2FB] text-[#2F6FBA]',
    coral: 'bg-[#FFF0EC] text-[#FD775E]',
};

function Chip({ tone, children }: { tone: Tone; children: ReactNode }) {
    return (
        <span
            className={`inline-flex flex-none items-center rounded-full px-2 py-0.5 text-[11px] font-bold ${TONE_CLASS[tone]}`}
        >
            {children}
        </span>
    );
}

/* ── Formatting helpers ───────────────────────────────────────── */

function fmt(n: number) {
    if (!Number.isFinite(n)) return '0';
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M`;
    if (n >= 10_000) return `${Math.round(n / 1_000)}K`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
    return n.toLocaleString();
}

function formatHour(hour?: number | null) {
    if (!Number.isFinite(Number(hour))) return '9:00 AM';
    return new Date(Date.UTC(2026, 0, 1, Number(hour), 0, 0, 0)).toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
        timeZone: 'UTC',
    });
}

function formatRelative(iso: string | null | undefined) {
    if (!iso) return '';
    const then = new Date(iso).getTime();
    const diff = Date.now() - then;
    if (Number.isNaN(diff) || diff < 0) return '';
    const minutes = Math.floor(diff / 60_000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    return new Date(iso).toLocaleDateString();
}

function dayLabel(index: number) {
    const ago = 6 - index;
    if (ago === 0) return 'Today';
    if (ago === 1) return 'Yesterday';
    return `${ago}d ago`;
}

/* ── Lane state derivations ───────────────────────────────────── */

function sourcingState(s: JobSourcingSummary): { tone: Tone; label: string } {
    if (s.lastRunStatus === 'failed') return { tone: 'red', label: 'Needs attention' };
    switch (s.status) {
        case 'active':
            return { tone: 'green', label: 'Daily active' };
        case 'paused':
            return { tone: 'gray', label: 'Paused' };
        case 'exhausted':
            return { tone: 'amber', label: 'Pool exhausted' };
        case 'off':
        default:
            return { tone: 'amber', label: 'Calibrating' };
    }
}

const OUTREACH_TONE: Partial<Record<JobOutreachSummary['status'], { tone: Tone; label: string }>> = {
    active: { tone: 'green', label: 'Active' },
    ready: { tone: 'coral', label: 'Ready' },
    paused: { tone: 'gray', label: 'Paused' },
    draft: { tone: 'blue', label: 'Draft' },
    completed: { tone: 'gray', label: 'Completed' },
    archived: { tone: 'gray', label: 'Archived' },
};

/* ── Page ─────────────────────────────────────────────────────── */

export function JobsListPage() {
    const navigate = useNavigate();
    const [jobs, setJobs] = useState<JobPosting[]>([]);
    const [summaries, setSummaries] = useState<Record<string, JobRosterSummary>>({});
    const [enriching, setEnriching] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [query, setQuery] = useState('');

    const fetchJobs = useCallback(async () => {
        setError(null);
        let results: JobPosting[] = [];
        try {
            const { data } = await jobApi.listJobs({
                notStatus: 'archived',
                limit: 100,
                sort: '-updatedAt',
            });
            results = data.results;
            setJobs(results);
        } catch (err) {
            setError(getApiErrorMessage(err, 'Could not load jobs.'));
        } finally {
            setLoading(false);
        }
        if (!results.length) return;
        // The list has painted — enrich every card off ONE batch call.
        setEnriching(true);
        try {
            const { data } = await jobApi.getRosterSummary(results.map((j) => j._id));
            setSummaries(data.summaries);
        } catch {
            // Non-fatal — cards simply render without lanes.
        } finally {
            setEnriching(false);
        }
    }, []);

    useEffect(() => {
        void fetchJobs();
    }, [fetchJobs]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return jobs;
        return jobs.filter((j) =>
            [j.title, j.department, j.location].filter(Boolean).some((v) => v!.toLowerCase().includes(q))
        );
    }, [jobs, query]);

    const metrics = useMemo(() => {
        const all = Object.values(summaries);
        if (!all.some((s) => s.sourcing || s.outreach)) return null;
        const sum = (pick: (s: JobRosterSummary) => number) => all.reduce((acc, s) => acc + pick(s), 0);
        return [
            {
                icon: Bot,
                label: 'Sourcing live',
                value: String(all.filter((s) => s.sourcing?.status === 'active').length),
                tone: 'blue' as Tone,
            },
            {
                icon: Eye,
                label: 'To review',
                value: String(sum((s) => s.sourcing?.pendingReviewCount ?? 0)),
                tone: 'amber' as Tone,
            },
            {
                icon: Sparkles,
                label: 'New today',
                value: String(sum((s) => s.sourcing?.newToday ?? 0)),
                tone: 'green' as Tone,
            },
            {
                icon: Users,
                label: 'In outreach',
                value: String(sum((s) => s.outreach?.total ?? 0)),
                tone: 'coral' as Tone,
            },
        ];
    }, [summaries]);

    return (
        <div className="relative isolate min-h-full w-full overflow-hidden bg-[#FAFAFA] px-8 pb-12 pt-7 font-[family-name:var(--font-sans)] text-[#1F2428] max-md:px-4">
            <RainbowBeamsBackdrop style={{ top: -360, width: '118%', opacity: 0.78 }} grainOpacity={0.32} />

            <header className="relative z-[1] flex flex-wrap items-center justify-between gap-4 rounded-lg border border-[rgba(153,165,178,0.22)] bg-[rgba(255,255,255,0.46)] px-5 py-4 shadow-[0_18px_50px_rgba(31,36,40,0.05)] backdrop-blur-[18px]">
                <div className="flex min-w-0 items-center gap-3">
                    <SidebarExpandButton style={{ marginLeft: -6 }} />
                    <span className="inline-flex h-[34px] w-[34px] flex-none items-center justify-center rounded-lg border border-[rgba(153,165,178,0.22)] bg-[rgba(255,255,255,0.72)] text-[#FD775E]">
                        <Briefcase size={17} strokeWidth={1.8} />
                    </span>
                    <div className="min-w-0">
                        <h1 className="m-0 font-[family-name:var(--font-serif)] text-[30px] font-medium leading-[1.08]">
                            Jobs
                        </h1>
                        <p className="mt-1 text-[13px] leading-snug text-[#66717C]">
                            Your open roles, with live sourcing and outreach at a glance.
                        </p>
                    </div>
                </div>

                <div className="relative">
                    <Search
                        size={14}
                        strokeWidth={1.8}
                        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#98A1AA]"
                    />
                    <input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Filter roles"
                        className="h-9 w-56 rounded-lg border border-[rgba(153,165,178,0.22)] bg-[rgba(255,255,255,0.72)] pl-8 pr-3 text-[13px] text-[#1F2428] outline-none placeholder:text-[#98A1AA] focus:border-[rgba(253,119,94,0.4)]"
                    />
                </div>
            </header>

            {metrics ? (
                <section
                    className="relative z-[1] mt-3.5 grid grid-cols-4 gap-2.5 rounded-lg border border-[rgba(153,165,178,0.22)] bg-[rgba(255,255,255,0.64)] p-2.5 shadow-[0_18px_50px_rgba(31,36,40,0.05)] backdrop-blur-[18px] max-md:grid-cols-2"
                    aria-label="Roster summary"
                >
                    {metrics.map((m) => (
                        <MetricCard key={m.label} {...m} />
                    ))}
                </section>
            ) : null}

            {error ? (
                <div className="relative z-[1] mt-3.5 rounded-lg bg-[#FFF0EE] px-3.5 py-2.5 text-[13px] font-semibold text-[#B83D2F]">
                    {error}
                </div>
            ) : null}

            {loading ? (
                <div className="relative z-[1] flex min-h-[300px] items-center justify-center gap-2 text-[13px] text-[#66717C]">
                    <Loader2 size={16} className="animate-spin" />
                    Loading jobs
                </div>
            ) : filtered.length === 0 ? (
                <EmptyState hasJobs={jobs.length > 0} />
            ) : (
                <div className="relative z-[1] mt-3.5 grid grid-cols-[repeat(auto-fill,minmax(340px,1fr))] gap-3">
                    {filtered.map((job) => (
                        <JobCard
                            key={job._id}
                            job={job}
                            summary={summaries[job._id] ?? null}
                            enriching={enriching}
                            onOpen={() => navigate(`/v2/jobs/${job._id}/manage`)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function MetricCard({
    icon: Icon,
    label,
    value,
    tone,
}: {
    icon: LucideIcon;
    label: string;
    value: string;
    tone: Tone;
}) {
    return (
        <div className="flex min-h-[64px] min-w-0 items-center gap-2.5 rounded-lg border border-[rgba(153,165,178,0.22)] bg-[rgba(255,255,255,0.76)] p-3">
            <span
                className={`inline-flex h-[30px] w-[30px] flex-none items-center justify-center rounded-lg ${TONE_CLASS[tone]}`}
            >
                <Icon size={15} strokeWidth={1.8} />
            </span>
            <div className="min-w-0">
                <strong className="block text-[19px] leading-none">{value}</strong>
                <small className="mt-1 block truncate text-[12px] text-[#66717C]">{label}</small>
            </div>
        </div>
    );
}

function EmptyState({ hasJobs }: { hasJobs: boolean }) {
    return (
        <div className="relative z-[1] mt-3.5 flex min-h-[300px] items-center justify-center rounded-lg border border-[rgba(153,165,178,0.22)] bg-[rgba(255,255,255,0.55)] p-10 backdrop-blur-[18px]">
            <div className="max-w-[420px] text-center">
                <span className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[rgba(255,255,255,0.85)] text-[#66717C] shadow-[0_8px_24px_rgba(31,36,40,0.08)]">
                    <Briefcase size={20} strokeWidth={1.6} />
                </span>
                <p className="m-0 text-[15px] font-semibold text-[#1F2428]">
                    {hasJobs ? 'No roles match your filter' : 'No jobs yet'}
                </p>
                <p className="mx-0 mb-0 mt-2 text-[13px] leading-relaxed text-[#66717C]">
                    {hasJobs
                        ? 'Try a different search term.'
                        : 'Start a session on the home screen and ask Lantern to open a role.'}
                </p>
            </div>
        </div>
    );
}

/* ── 7-day sourced sparkline (quantized static heights) ───────── */

const BAR_HEIGHTS = ['h-[4px]', 'h-[7px]', 'h-[10px]', 'h-[13px]', 'h-[16px]', 'h-[19px]', 'h-[22px]'];

function Sparkline({ buckets }: { buckets: number[] }) {
    const days = buckets.length === 7 ? buckets : Array(7).fill(0);
    const total = days.reduce((sum, n) => sum + n, 0);
    if (!total) return null;
    const max = Math.max(...days, 1);
    return (
        <span className="inline-flex flex-none items-center gap-1.5">
            <span className="flex h-[22px] items-end gap-[3px]" aria-label={`7-day sourced: ${total} total`}>
                {days.map((value, index) => (
                    <span
                        key={index}
                        title={`${dayLabel(index)}: ${value} sourced`}
                        className={`block w-[6px] rounded-t-full ${
                            value === 0
                                ? 'h-[4px] bg-[#1E7F5F]/20'
                                : `${BAR_HEIGHTS[Math.min(6, Math.max(1, Math.round((value / max) * 6)))]} bg-[#1E7F5F]/70`
                        }`}
                    />
                ))}
            </span>
            <span className="text-[11px] font-semibold text-[#66717C]">{total}/7d</span>
        </span>
    );
}

/* ── Cards ────────────────────────────────────────────────────── */

function LaneRow({
    icon,
    title,
    chip,
    right,
    detail,
}: {
    icon: ReactNode;
    title: string;
    chip: { tone: Tone; label: string };
    right?: ReactNode;
    detail: ReactNode;
}) {
    return (
        <div className="border-t border-[rgba(153,165,178,0.18)] pt-2.5">
            <div className="flex items-center gap-2">
                <span className="inline-flex h-6 w-6 flex-none items-center justify-center rounded-md bg-[rgba(255,255,255,0.9)] text-[#66717C] shadow-[0_1px_3px_rgba(31,36,40,0.08)]">
                    {icon}
                </span>
                <span className="text-[12px] font-semibold text-[#1F2428]">{title}</span>
                <Chip tone={chip.tone}>{chip.label}</Chip>
                <span className="ml-auto flex min-w-0 items-center">{right}</span>
            </div>
            <div className="mt-1.5 flex min-w-0 items-center gap-1.5 pl-8 text-[12px] text-[#66717C]">{detail}</div>
        </div>
    );
}

function SourcingLane({ s }: { s: JobSourcingSummary }) {
    const state = sourcingState(s);
    const bits: string[] = [];
    if (s.poolTotal) bits.push(`${fmt(s.poolTotal)} pool`);
    if (s.deliveredCount) bits.push(`${fmt(s.deliveredCount)} sourced`);
    if (s.status === 'active') bits.push(`${formatHour(s.scheduleHour)} · ${s.dailyTarget || 10}/day`);
    return (
        <LaneRow
            icon={<Radar size={13} strokeWidth={1.8} />}
            title="Sourcing"
            chip={state}
            right={<Sparkline buckets={s.sourced7d ?? []} />}
            detail={
                <>
                    <span className="truncate">{bits.join(' · ') || 'No candidates yet'}</span>
                    {s.pendingReviewCount > 0 ? <Chip tone="coral">{s.pendingReviewCount} to review</Chip> : null}
                </>
            }
        />
    );
}

function OutreachLane({ o }: { o: JobOutreachSummary }) {
    const state = OUTREACH_TONE[o.status] ?? {
        tone: 'gray' as Tone,
        label: o.status,
    };
    const byStage = o.byStage ?? {};
    const bits: string[] = [];
    if (o.total) bits.push(`${fmt(o.total)} prospects`);
    if (byStage.active) bits.push(`${byStage.active} active`);
    if (byStage.replied) bits.push(`${byStage.replied} replied`);
    return (
        <LaneRow
            icon={<Send size={13} strokeWidth={1.8} />}
            title="Outreach"
            chip={state}
            detail={<span className="truncate">{bits.join(' · ') || 'No prospects yet'}</span>}
        />
    );
}

function JobCard({
    job,
    summary,
    enriching,
    onOpen,
}: {
    job: JobPosting;
    summary: JobRosterSummary | null;
    enriching: boolean;
    onOpen: () => void;
}) {
    const metaBits = [job.department, job.location].filter(Boolean) as string[];
    const sourcing = summary?.sourcing ?? null;
    const outreach = summary?.outreach ?? null;

    return (
        <button
            type="button"
            onClick={onOpen}
            className="group relative flex flex-col gap-2.5 overflow-hidden rounded-lg border border-[rgba(153,165,178,0.22)] bg-[rgba(255,255,255,0.78)] p-4 text-left shadow-[0_18px_50px_rgba(31,36,40,0.04)] backdrop-blur-[18px] transition-all duration-150 hover:border-[rgba(253,119,94,0.28)] hover:bg-[rgba(255,255,255,0.92)] hover:shadow-[0_14px_34px_rgba(31,36,40,0.08)]"
        >
            <span className="pointer-events-none absolute bottom-2.5 left-0 top-2.5 w-[2px] rounded-full bg-transparent transition-colors duration-150 group-hover:bg-[#FD775E]" />
            <div className="min-w-0">
                <h2 className="m-0 truncate text-[14.5px] font-semibold text-[#1F2428]">
                    {job.title || 'Untitled role'}
                </h2>
                {metaBits.length ? (
                    <p className="mt-1 flex items-center gap-1.5 truncate text-[12px] text-[#98A1AA]">
                        {job.location ? <MapPin size={11} strokeWidth={1.8} /> : null}
                        {metaBits.join(' · ')}
                    </p>
                ) : null}
            </div>

            {enriching ? (
                <div className="flex animate-pulse flex-col gap-2 border-t border-[rgba(153,165,178,0.18)] pt-3">
                    <div className="h-[10px] w-2/3 rounded-full bg-[rgba(153,165,178,0.18)]" />
                    <div className="h-[10px] w-1/2 rounded-full bg-[rgba(153,165,178,0.14)]" />
                </div>
            ) : (
                <>
                    {sourcing ? <SourcingLane s={sourcing} /> : null}
                    {outreach ? <OutreachLane o={outreach} /> : null}
                </>
            )}

            <div className="mt-auto text-[11px] text-[#98A1AA]">Updated {formatRelative(job.updatedAt)}</div>
        </button>
    );
}

export default JobsListPage;
